import { CardsState } from '../Models/CardsState'

const STATE_KEY = 'solitaire-game-state'

// ─── Debounced save ─────────────────────────────────────────────────────────

let _saveTimer: ReturnType<typeof setTimeout> | null = null
let _pendingState: CardsState[] | null = null
const SAVE_DEBOUNCE_MS = 300

/** Internal: perform the actual localStorage write. */
function writeToDisk(state: CardsState[]): void {
    try {
        if (state && state.length > 0) {
            localStorage.setItem(STATE_KEY, JSON.stringify(state))
        }
    } catch (error) {
        console.error('Failed to save game state:', error)
    }
}

/**
 * Saves the game state (cardState history) to localStorage.
 * Debounced — rapid consecutive calls within 300ms are coalesced into a single write.
 * @param state - Array of game states representing the history
 */
export function saveGameState(state: CardsState[]): void {
    _pendingState = state
    if (_saveTimer !== null) {
        clearTimeout(_saveTimer)
    }
    _saveTimer = setTimeout(() => {
        _saveTimer = null
        if (_pendingState) {
            writeToDisk(_pendingState)
            _pendingState = null
        }
    }, SAVE_DEBOUNCE_MS)
}

/**
 * Flush any pending debounced save immediately.
 * Call before page unload or when immediate persistence is critical.
 */
export function flushGameState(): void {
    if (_saveTimer !== null) {
        clearTimeout(_saveTimer)
        _saveTimer = null
    }
    if (_pendingState) {
        writeToDisk(_pendingState)
        _pendingState = null
    }
}

/**
 * Loads the saved game state from localStorage.
 * Validates the structure before returning — returns null if the data
 * is missing, corrupt, or doesn't match the expected CardsState shape.
 * @returns Saved game state array, or null if no valid saved state exists
 */
export function loadGameState(): CardsState[] | null {
    try {
        const stored = localStorage.getItem(STATE_KEY)
        if (stored) {
            const parsed = JSON.parse(stored) as CardsState[]
            // Validate the loaded state is an array with at least one entry
            if (!Array.isArray(parsed) || parsed.length === 0) return null

            // Spot-check the last entry (current state) for expected properties
            const last = parsed[parsed.length - 1]
            if (
                !last ||
                !Array.isArray(last.foundation) ||
                last.foundation.length !== 4 ||
                !Array.isArray(last.stock) ||
                !Array.isArray(last.waste) ||
                !Array.isArray(last.tableau) ||
                last.tableau.length !== 7
            ) {
                console.warn('Saved game state failed structural validation, discarding')
                return null
            }

            return parsed
        }
    } catch (error) {
        console.warn('Failed to load game state:', error)
    }
    return null
}

/**
 * Clears the saved game state from localStorage
 */
export function clearGameState(): void {
    try {
        localStorage.removeItem(STATE_KEY)
    } catch (error) {
        console.error('Failed to clear game state:', error)
    }
}
